import React, { useState, useEffect } from 'react';
import { useAuth, API_URL, Currency, DateBR } from '../contexts/AuthContext';
import { TrendingUp, Search, Download } from 'lucide-react';

export default function Relatorios() {
  const { token } = useAuth();
  const [summary, setSummary] = useState<any>(null);
  const [salesDaily, setSalesDaily] = useState<any[]>([]);
  const [expDaily, setExpDaily] = useState<any[]>([]);
  const [top, setTop] = useState<any[]>([]);
  const [audit, setAudit] = useState<any[]>([]);
  const [period, setPeriod] = useState({ start: '', end: '' });

  useEffect(() => {
    if (!token) return;
    const load = () => {
      fetch(`${API_URL}/reports/summary`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setSummary(d)).catch(() => {});
      fetch(`${API_URL}/reports/sales-daily?days=30`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setSalesDaily(d)).catch(() => {});
      fetch(`${API_URL}/reports/expenses-daily?days=30`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setExpDaily(d)).catch(() => {});
      fetch(`${API_URL}/reports/top-products?limit=10`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setTop(d)).catch(() => {});
      fetch(`${API_URL}/reports/audit?limit=20`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setAudit(d)).catch(() => {});
    };
    load();
  }, [token]);

  const exportCSV = (data: any[], filename: string) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map(o => headers.map(h => JSON.stringify(o[h])).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
        <p className="text-gray-600">Relatórios gerenciais e exportação de dados</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card"><div className="text-xs text-gray-500 uppercase">Faturamento</div><div className="text-2xl font-bold">{summary ? Currency(summary.faturamento) : '–'}</div></div>
        <div className="card"><div className="text-xs text-gray-500 uppercase">Lucro bruto</div><div className="text-2xl font-bold text-green-600">{summary ? Currency(summary.lucro_bruto) : '–'}</div></div>
        <div className="card"><div className="text-xs text-gray-500 uppercase">Lucro líquido</div><div className="text-2xl font-bold">{summary ? Currency(summary.lucro_liquido) : '–'}</div></div>
        <div className="card"><div className="text-xs text-gray-500 uppercase">Estoque (custo)</div><div className="text-2xl font-bold">{summary ? Currency(summary.estoque_custo) : '–'}</div></div>
        <div className="card"><div className="text-xs text-gray-500 uppercase">A receber</div><div className="text-2xl font-bold">{summary ? Currency(summary.a_receber) : '–'}</div></div>
        <div className="card"><div className="text-xs text-gray-500 uppercase">A pagar</div><div className="text-2xl font-bold">{summary ? Currency(summary.a_pagar) : '–'}</div></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Vendas por dia (30 dias)</h2>
          <div className="space-y-2">
            {salesDaily.map(s => (
              <div key={s.data} className="flex items-center justify-between py-1 text-sm border-b border-gray-100 last:border-0">
                <span>{s.dia}</span><span className="font-medium text-right">{Currency(s.total)}</span>
              </div>
            ))}
          </div>
          <button onClick={() => exportCSV(salesDaily, 'vendas-diarias.csv')} className="mt-3 text-xs flex items-center gap-1 text-blue-600"><Download size={14} /> Exportar CSV</button>
        </div>
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Despesas por dia (30 dias)</h2>
          <div className="space-y-2">
            {expDaily.map(s => (
              <div key={s.data} className="flex items-center justify-between py-1 text-sm border-b border-gray-100 last:border-0">
                <span>{s.dia}</span><span className="font-medium text-right text-red-600">{Currency(s.total)}</span>
              </div>
            ))}
          </div>
          <button onClick={() => exportCSV(expDaily, 'despesas-diarias.csv')} className="mt-3 text-xs flex items-center gap-1 text-blue-600"><Download size={14} /> Exportar CSV</button>
        </div>
      </div>
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Produtos mais vendidos</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-gray-200"><th className="pb-2 text-gray-500">Produto</th><th className="pb-2 text-gray-500 text-right">Qtd</th><th className="pb-2 text-gray-500 text-right">Faturamento</th></tr></thead>
            <tbody>
              {top.map(p => (
                <tr key={p.name} className="border-b border-gray-100 last:border-0">
                  <td className="py-2">{p.name}</td>
                  <td className="py-2 text-right">{p.qtd_vendida}</td>
                  <td className="py-2 text-right">{Currency(p.faturamento)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Auditoria — últimas operações</h2>
        <div className="space-y-2">
          {audit.map(a => (
            <div key={a.id} className="flex items-center justify-between py-1 text-xs border-b border-gray-100 last:border-0">
              <div>
                <div className="text-gray-700">{a.action}</div>
                <div className="text-gray-500">{a.entity} #{a.entity_id} · {a.user_name}</div>
              </div>
              <div className="text-right text-gray-500">{new Date(a.created_at).toLocaleString('pt-BR')}</div>
            </div>
          ))}
          {audit.length === 0 && <p className="text-xs text-gray-500 text-center py-4">Sem auditoria</p>}
        </div>
      </div>
    </div>
  );
}
